import React from 'react';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import SelectLinkTypeView from 'Components/Modals/DocumentLink/SelectLinkTypeView';
import LinkFileView from 'Components/Modals/DocumentLink/LinkFileView';
import { Box, DialogTitle } from '@material-ui/core';
import { IconButton } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import DialogContentText from '@material-ui/core/DialogContentText';
import MetaFormInline from 'Components/UI/Meta/MetaForm/Components/MetaFormInline';
import TrustApprovalLayout from 'Components/Modals/TrustApproval/Layout';
import DataContainer from 'Components/UI/DataContainer';
import fr from 'facet-rest';
import PropTypes from 'prop-types';
import Grow from '@mui/material/Grow';
import LoadingOverlay from 'Components/UI/LoadingOverlay/LoadingOverlay';
import { noop } from 'Helpers/PropTypes';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import { CollectionProvider, useCollection } from 'Context/CollectionContext';
import TrustForm from 'Components/Modals/TrustApproval/TrustForm';
import PrimaryTrusteeForm from 'Components/Modals/TrustApproval/PrimaryTrusteeForm';
import AdditionalTrusteeForm from 'Components/Modals/TrustApproval/AdditionalTrusteeForm';

const styles = theme => ({
  root: {
    display: 'flex',
    justifyContent: 'center',
    minWidth: theme.dimensions.sm,
    minHeight: theme.dimensions.sm,
  },
  icon: {
    left: 16,
    position: 'absolute',
    padding: theme.spacing(0.5),
  },
  title: {
    padding: theme.spacing(1),
    fontSize: 24,
    textAlign: 'center',
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightMedium,
  },
  text: {
    margin: theme.spacing(0, 3),
    fontSize: 14,
    textAlign: 'center',
    color: theme.palette.facet.primaryGray,
    fontWeight: theme.typography.fontWeightRegular,
  },
});

const SELECT_FILE = 'select-file';
const SELECT_UPLOAD_TYPE = 'select-upload-type';
const UPDATE_TRUST = 'update-trust';
const UPDATE_TRUSTEE = 'update-trustee';
const UPDATE_ADDITIONAL_TRUSTEE = 'update-additional-trustee';

const GrowTab = (props) => (
  <Grow in>
    <Box {...props} />
  </Grow>
);

const TrustApprovalTab = withStyles(styles)(({ classes, onClose }) => {
  const { collection: approvalCollection, refresh: refreshApprovalCollection } = useCollection();
  const approval = approvalCollection?.resources().head();

  const [{ uploadRelationship, fileOptionListRelationship }, setUploadRelationships] = React.useState({
    uploadRelationship: null,
    fileOptionListRelationship: null,
  });

  const [tab, setTab] = React.useState();

  const [index, setIndex] = React.useState(null);

  switch (tab) {
    case UPDATE_TRUST: {
      return (
        <GrowTab>
          <DialogTitle classes={{ root: classes.title }} disableTypography>
            <IconButton
              onClick={() => setTab()}
              className={classes.icon}
              color="primary"
              edge="start"
              icon={ChevronLeftIcon}
              size="sm"
              title="Go back"
            />
            {'Enter Trust Information'}
          </DialogTitle>
          <CollectionProvider link={approval?.relationship(fr.relationships.trust.name).link('related').url()}>
            <TrustForm onSubmit={() => {
              setTab();
              refreshApprovalCollection();
            }}
            />
          </CollectionProvider>
        </GrowTab>
      );
    }
    case UPDATE_TRUSTEE: {
      return (
        <GrowTab>
          <DialogTitle classes={{ root: classes.title }} disableTypography>
            <IconButton
              onClick={() => setTab()}
              className={classes.icon}
              color="primary"
              edge="start"
              icon={ChevronLeftIcon}
              size="sm"
              title="Go back"
            />
            {'Enter Trustee Information'}
          </DialogTitle>
          <CollectionProvider link={approval?.relationship(fr.relationships.trustee.name).link('related').url()}>
            <PrimaryTrusteeForm onSubmit={() => {
              setTab();
              refreshApprovalCollection();
            }}
            />
          </CollectionProvider>
        </GrowTab>
      );
    }
    case UPDATE_ADDITIONAL_TRUSTEE: {
      return (
        <GrowTab>
          <DialogTitle classes={{ root: classes.title }} disableTypography>
            <IconButton
              onClick={() => setTab()}
              className={classes.icon}
              color="primary"
              edge="start"
              icon={ChevronLeftIcon}
              size="sm"
              title="Go back"
            />
            {'Additional Trustees'}
          </DialogTitle>
          <CollectionProvider link={approval?.relationship(fr.relationships.additionalTrustees.name).link('related').url()}>
            <AdditionalTrusteeForm
              index={index}
              onSubmit={() => {
                setTab();
                setIndex(null);
                refreshApprovalCollection();
              }}
            />
          </CollectionProvider>
        </GrowTab>
      );
    }
    case SELECT_FILE:
      return (
        <GrowTab>
          <DialogTitle classes={{ root: classes.title }} disableTypography>
            <IconButton
              onClick={() => setTab()}
              className={classes.icon}
              color="primary"
              edge="start"
              icon={ChevronLeftIcon}
              size="sm"
              title="Go back"
            />
            {'Select a file'}
          </DialogTitle>
          <LinkFileView
            classes={{ root: classes.root }}
            fileOptionLink={fileOptionListRelationship}
            onLink={() => {
              setTab();
              refreshApprovalCollection();
            }}
          />
        </GrowTab>
      );
    case SELECT_UPLOAD_TYPE:
      return (
        <GrowTab>
          <DialogTitle classes={{ root: classes.title }} disableTypography>
            <IconButton
              onClick={() => setTab()}
              className={classes.icon}
              color="primary"
              edge="start"
              icon={ChevronLeftIcon}
              size="sm"
              title="Go back"
            />
            {'Select Type of Document'}
          </DialogTitle>
          <SelectLinkTypeView
            classes={{ root: classes.root }}
            uploadRelationship={uploadRelationship}
            onSelectFile={() => setTab(SELECT_FILE)}
            onComplete={() => {
              setTab();
              refreshApprovalCollection();
            }}
          />
        </GrowTab>
      );
    default:
      return (
        <GrowTab>
          <DialogTitle classes={{ root: classes.title }} disableTypography>
            {'Trust Document Approval'}
          </DialogTitle>
          <DialogContentText className={classes.text}>
              Pressing send will send all the files to an Investment Specialist for review.<br />This can not be undone.
          </DialogContentText>
          {approval && (<MetaFormInline
            resource={approval}
            layout={TrustApprovalLayout}
            onSubmit={onClose}
            LayoutProps={{
              onUpdateTrust: () => {
                setTab(UPDATE_TRUST);
              },
              onUpdateTrustee: () => {
                setTab(UPDATE_TRUSTEE);
              },
              onUpdateAdditionalTrustees: (r) => {
                setIndex(r?.parent().list().findIndex(x => x.id() === r.id()));
                setTab(UPDATE_ADDITIONAL_TRUSTEE);
              },
              onError: (e) => displayErrorMessage(e),
              onLoad: refreshApprovalCollection,
              onTrustUpload: () => {
                setUploadRelationships({
                  uploadRelationship: approval.relationship(fr.relationships.trustDocument.name)
                    .link('related')
                    .url(),
                  fileOptionListRelationship: approval.relationship(fr.relationships.linkTrustDocument.name)
                    .link('related')
                    .url(),
                });
                setTab(SELECT_UPLOAD_TYPE);
              },
              onOtherUpload: () => {
                setUploadRelationships({
                  uploadRelationship: approval.relationship(fr.relationships.accountDocuments.name)
                    .link('related')
                    .url(),
                  fileOptionListRelationship: approval.relationship(fr.relationships.linkAccountDocument.name)
                    .link('related')
                    .url(),
                });
                setTab(SELECT_UPLOAD_TYPE);
              },
            }}
            noIndividualSubmitButtons
          />
          )}
        </GrowTab>
      );
  }
});

function TrustApprovalContent({ classes, onClose }) {
  const { collection: approvalCollection, initializingError, isRefreshing } = useCollection();

  return (
    <DataContainer classes={{ root: classes.root }} initializingError={initializingError} loading={approvalCollection === null}>
      {isRefreshing && <LoadingOverlay />}
      <Box paddingY={2} paddingX={4} className={classes.content}>
        <TrustApprovalTab onClose={onClose} />
      </Box>
    </DataContainer>
  );
}

TrustApprovalContent.defaultProps = {
  onClose: noop,
};

TrustApprovalContent.propTypes = {
  classes: PropTypes.object.isRequired,
  onClose: PropTypes.func,
};

export default withStyles(theme => ({
  root: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    alignContent: 'center',
    width: '100%',
    minHeight: theme.dimensions.md,
    minWidth: theme.dimensions.sm,
  },
  content: {
    minHeight: theme.dimensions.md,
    minWidth: theme.dimensions.sm,
  },
}))(TrustApprovalContent);
